import React, { PropsWithChildren } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './ResourcesForYou.css';
import { Article } from '../../types/shared/Article';
import { shortenText, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import {Button, Section, PhotoCard, Title } from '../../importCL';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';

const DEFAULT_IMAGE_WIDTH = 360;
const DEFAULT_IMAGE_HEIGHT = 240;

interface IResourcesForYouItem {
    article?: Article;
    isFeaturedArticle?: boolean;
}

export interface IResourcesForYouProps extends MappedComponentProperties {
    ctaLink?: string;
    ctaText?: string;
    items?: IResourcesForYouItem[];
    articleCtaText?: string;
    useChevronLinks?: boolean;
    bgColor?: string;
    hrColor?: string;
    titleColor?: string;
    title?: string;
    subtitle?: string;
    titleSize?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
    titleAlignment?: 'center' | 'left';
}

export const ResourcesForYouEditConfig = {
    emptyLabel: 'ResourcesForYou',
    isEmpty: (props: PropsWithChildren<IResourcesForYouProps>) => {
        return !props || (props.items && props.items.length === 0);
    },
};

const ResourcesForYou = (props: PropsWithChildren<IResourcesForYouProps>): JSX.Element => {
    
    const darkBackground = props.bgColor === 'purple' || props.bgColor === 'blue-creditone';
    const titleTag = props.titleSize || 'h2';
    const titleAlignment = props.titleAlignment || 'left';

    return (
        <Section id='resources-for-you' className={`${props.bgColor ? `bg-${props.bgColor}` : ''} ${darkBackground ? 'darkMode' : ''}`}>
                {props.title &&
                    <Title
                        tag={titleTag}
                        spanTitle={props.subtitle && transformHTMLParserLinks(props.subtitle)}
                        verticalBar={titleTag === 'h2' && titleAlignment === 'left'}
                        align={titleAlignment}
                    >
                        {transformHTMLParserLinks(wrapTrademarkSuperscript(props.title))}
                    </Title>
                }
            <div className='ResourcesForYou__articles'>
                {props.items?.map((item, index) => {
                    if(item?.article) {
                        return (
                            <PhotoCard 
                                className={`ResourcesForYou__photoCard${darkBackground ? '--darkMode' : ''}`}
                                tagClass={`ResourcesForYou__photoCardContainer${index === 1 ? '--center' : ''}`} 
                                key={index}
                                anchorText={props.articleCtaText}
                                tag='article'
                                chevron={props.useChevronLinks ?? false}
                                titleTag="h4"
                                showAnchorMobile
                                shadowless
                                cardContent={{
                                    helperHeader: new Date(item.article?.date).toDateString(),
                                    title: `${item.article!.title}`,
                                    description: `${shortenText(item.article!.previewDescription as string)}`,
                                    image: `${item.article!.imagePath + '?imwidth=360'}`,
                                    imageAltText: `${item.article!.imageAlt}`,
                                    imageWidth: DEFAULT_IMAGE_WIDTH,
                                    imageHeight: DEFAULT_IMAGE_HEIGHT,
                                    link: `${item.article!.pagePath}`,
                                    target: '_self',
                            }}/>
                        );
                    }
                }
                )}
            </div>
            <div className='ResourcesForYou__viewMoreBtn' >
                {props.ctaText &&
                    <Button 
                        variant={`${darkBackground ? 'link-light' : 'link'}`}
                        icon='chevron' 
                        size='medium' 
                        href={props.ctaLink}>
                            {props.ctaText}
                    </Button>}
            </div>
        </Section>
    );
};

export default ResourcesForYou;
